import create from 'zustand';
import shallow from 'zustand/shallow';
import { AllocationsDto, PeriodDataDto, EntryDto, ProjectDtoV2, TaskDtoV2, WorkingHoursDto } from '../../api/generated/v2/generated/data-contracts';
import { FunctionOr, unwrap } from '../../_helpers/functionOr';
import { getLocalStorage, setLocalStorage } from '../../helpers/timeLocalStorage';
import Big, { bigZero } from '../../math/math';

// ============================================================================
// Custom Equality Functions
// ============================================================================

/** Value-based equality for Big instances (avoids unnecessary re-renders from new object references) */
const bigEq = (a: Big, b: Big): boolean => a.eq(b);

/** Shallow equality for Record<string, Big> — compares keys and Big values */
const shallowBigRecord = (a: Record<string, Big>, b: Record<string, Big>): boolean => {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => key in b && a[key].eq(b[key]));
};

/** Deep equality for EntryWithBigValue — compares Big value, flagged, and comment */
const entryEq = (a: EntryWithBigValue | undefined, b: EntryWithBigValue | undefined): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.value.eq(b.value) && a.flagged === b.flagged && a.comment === b.comment;
};

// ============================================================================
// LocalStorage Helpers for Expand/Collapse Persistence
// ============================================================================

export const EXPANDED_PROJECTS_KEY = 'expandedProjectIds';
const MAX_EXPANDED_IDS = 100;

/** Load expanded project IDs from localStorage, intersected with the current period's projects. */
const loadExpandedIds = (currentProjectIds: string[]): Set<string> => {
    const stored = getLocalStorage<string[]>(EXPANDED_PROJECTS_KEY);
    if (!Array.isArray(stored)) return new Set<string>();
    const currentSet = new Set(currentProjectIds);
    return new Set(stored.filter(id => currentSet.has(id)));
};

/** Persist expanded project IDs to localStorage using a merge strategy: keep IDs from other periods, update IDs from the current period. Caps at MAX_EXPANDED_IDS. */
const saveExpandedIds = (currentProjectIds: string[], expandedIds: Set<string>) => {
    const stored = getLocalStorage<string[]>(EXPANDED_PROJECTS_KEY);
    const existing = Array.isArray(stored) ? stored : [];
    const currentSet = new Set(currentProjectIds);
    // Keep IDs not in the current period (from other periods)
    const otherPeriodIds = existing.filter(id => !currentSet.has(id));
    // Add currently expanded IDs at the end
    const merged = [...otherPeriodIds, ...expandedIds];
    // Cap at max length, keeping newest (at end)
    const capped = merged.length > MAX_EXPANDED_IDS ? merged.slice(merged.length - MAX_EXPANDED_IDS) : merged;
    setLocalStorage(EXPANDED_PROJECTS_KEY, capped);
};

// ============================================================================
// Types
// ============================================================================

export type StateSetter<T> = (valueOrUpdate: FunctionOr<T>) => void;

export type SortField = 'date' | 'value' | 'taskName' | 'projectName';
export type SortDirection = 'asc' | 'desc';

// Type transformation: Convert entry value from number to Big for precision
export type EntryWithBigValue = Omit<EntryDto, 'value'> & { value: Big };

// Store state types
export type GridStoreState = {
    periodData: PeriodDataDto | null;
    entriesMap: Map<string, EntryWithBigValue>;
    projects: ProjectDtoV2[];
    tasks: TaskDtoV2[];
    workingHours: WorkingHoursDto[];
    expandedProjectIds: Set<string>;
    pinnedTaskKeys: Set<string>;
    allocationInfo: AllocationsDto | null;
    highlightedTaskId: string | null;
    showSubmitSuccess: boolean;
};

// Action types
export type GridStoreActions = {
    // Initialization
    initState: (data: PeriodDataDto) => () => void;
    cleanup: () => void;

    // Period data
    setPeriodData: StateSetter<PeriodDataDto | null>;

    // Entry CRUD
    upsertEntry: (entry: EntryWithBigValue) => void;
    removeEntry: (entryKey: string) => void;
    batchAddEntries: (entries: EntryDto[]) => void;
    batchUpsertEntries: (entries: EntryWithBigValue[]) => void;

    // Task CRUD
    addTask: (task: TaskDtoV2) => void;
    updateTask: (taskId: string, workType: string, updates: Partial<TaskDtoV2>) => void;
    removeTask: (taskId: string, workType?: string) => void;

    // Project CRUD
    addProject: (project: ProjectDtoV2) => void;
    updateProject: (projectId: string, updates: Partial<ProjectDtoV2>) => void;

    // Expand/Collapse
    toggleProjectExpanded: (projectId: string) => void;
    setProjectExpanded: (projectId: string, expanded: boolean) => void;
    expandAllProjects: () => void;
    collapseAllProjects: () => void;

    // Pin
    toggleTaskPinned: (taskId: string, workType: string) => void;
    setTaskPinned: (taskId: string, workType: string, pinned: boolean) => void;

    // Highlight
    setHighlightedTask: (taskId: string | null) => void;

    // Submit success
    setShowSubmitSuccess: (show: boolean) => void;
};

// Full store type
export type GridStore = GridStoreState & GridStoreActions;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate an array of YYYY-MM-DD date strings from startDate to endDate (inclusive).
 * Extracts the date portion directly from the ISO string to avoid timezone shifts.
 */
function generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    // Extract YYYY-MM-DD directly from the string to avoid timezone conversion issues
    const startDateOnly = startDate.slice(0, 10);
    const endDateOnly = endDate.slice(0, 10);

    // Parse as UTC noon to avoid any DST edge cases during iteration
    const current = new Date(`${startDateOnly}T12:00:00Z`);
    const end = new Date(`${endDateOnly}T12:00:00Z`);

    while (current <= end) {
        dates.push(current.toISOString().slice(0, 10));
        current.setUTCDate(current.getUTCDate() + 1);
    }
    return dates;
}

/**
 * Normalize a date-time string to YYYY-MM-DD format.
 * Handles both "2026-02-09" and "2026-02-09T00:00:00" formats.
 */
function normalizeDate(date: string): string {
    return date.slice(0, 10);
}

/**
 * Generate composite key for entries
 * Format: taskId_date_workTypeId
 */
export function getEntryKey(entry: Pick<EntryDto | EntryWithBigValue, 'taskId' | 'date' | 'workTypeId'>): string {
    return `${entry.taskId}_${normalizeDate(entry.date)}_${entry.workTypeId || 'default'}`;
}

/**
 * Convert an array of EntryDto (with Big values) into a Map keyed by composite key
 */
function buildEntriesMap(entries: EntryWithBigValue[]): Map<string, EntryWithBigValue> {
    const map = new Map<string, EntryWithBigValue>();
    for (const entry of entries) {
        map.set(getEntryKey(entry), entry);
    }
    return map;
}

/**
 * Create placeholder EntryDto entries for every task × date combination
 * that doesn't already exist in the entries array.
 * Returns a new array with the original entries plus any missing placeholders.
 * This ensures useEntry always returns a defined EntryWithBigValue after mapping.
 */
function fillMissingEntries(entries: EntryDto[], tasks: TaskDtoV2[], dates: string[], userPeriodId: string): EntryDto[] {
    const existingKeys = new Set(entries.map(entry => getEntryKey(entry)));
    const missing: EntryDto[] = [];

    for (const task of tasks) {
        for (const date of dates) {
            const key = `${task.id}_${date}_${task.workType || 'default'}`;
            if (!existingKeys.has(key)) {
                missing.push({
                    userPeriodId,
                    taskId: task.id,
                    date,
                    value: 0,
                    workTypeId: task.workType,
                    flagged: false,
                    comment: '',
                });
            }
        }
    }

    return missing.length > 0 ? [...entries, ...missing] : entries;
}

/**
 * Get entries array from the Map (for selectors that need iteration)
 */
function entriesArray(map: Map<string, EntryWithBigValue>): EntryWithBigValue[] {
    return Array.from(map.values());
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useGridStore = create<GridStore>((set, get) => ({
    // Initial state
    periodData: null,
    entriesMap: new Map<string, EntryWithBigValue>(),
    projects: [],
    tasks: [],
    workingHours: [],
    expandedProjectIds: new Set<string>(),
    pinnedTaskKeys: new Set<string>(),
    allocationInfo: null,
    highlightedTaskId: null,
    showSubmitSuccess: false,

    // ========================================================================
    // Initialization Actions
    // ========================================================================

    initState: (data: PeriodDataDto) => {
        const dates = generateDateRange(data.startDate, data.endDate);
        const allEntries = fillMissingEntries(data.entries, data.tasks, dates, data.id);

        const bigEntries = allEntries.map(entry => ({
            ...entry,
            date: normalizeDate(entry.date),
            value: new Big(entry.value?.toString() || '0'),
        }));

        set({
            periodData: data,
            entriesMap: buildEntriesMap(bigEntries),
            projects: data.projects,
            tasks: data.tasks,
            workingHours: data.workingHours,
            expandedProjectIds: loadExpandedIds(data.projects.map(project => project.id)),
            pinnedTaskKeys: new Set(data.tasks.filter(t => t.pinned).map(t => `${t.id}_${t.workType || 'default'}`)),
            allocationInfo: data.allocationInfo ?? null,
        });

        return get().cleanup;
    },

    cleanup: () => {
        set({
            periodData: null,
            entriesMap: new Map<string, EntryWithBigValue>(),
            projects: [],
            tasks: [],
            workingHours: [],
            expandedProjectIds: new Set<string>(),
            pinnedTaskKeys: new Set<string>(),
            allocationInfo: null,
            highlightedTaskId: null,
            showSubmitSuccess: false,
        });
    },

    // ========================================================================
    // Period Data Actions
    // ========================================================================

    setPeriodData: valueOrUpdater => {
        set(state => ({
            ...state,
            periodData: unwrap(valueOrUpdater, state.periodData),
        }));
    },

    // ========================================================================
    // Entry CRUD Actions
    // ========================================================================

    upsertEntry: (entry: EntryWithBigValue) => {
        const entryKey = getEntryKey(entry);
        const dtoEntry: EntryDto = {
            ...entry,
            value: Number(entry.value.toString()),
        };

        set(state => {
            const newMap = new Map(state.entriesMap);
            newMap.set(entryKey, entry);

            const exists = state.periodData?.entries.some(e => getEntryKey(e) === entryKey);

            return {
                entriesMap: newMap,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          entries: exists
                              ? state.periodData.entries.map(e => (getEntryKey(e) === entryKey ? dtoEntry : e))
                              : [...state.periodData.entries, dtoEntry],
                      }
                    : null,
            };
        });
    },

    removeEntry: (entryKey: string) => {
        set(state => {
            const newMap = new Map(state.entriesMap);
            newMap.delete(entryKey);

            return {
                ...state,
                entriesMap: newMap,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          entries: state.periodData.entries.filter(entry => getEntryKey(entry) !== entryKey),
                      }
                    : null,
            };
        });
    },

    batchAddEntries: (entries: EntryDto[]) => {
        const entriesWithBig = entries.map(entry => ({
            ...entry,
            date: normalizeDate(entry.date),
            value: new Big(entry.value.toString()),
        }));

        set(state => {
            const newMap = new Map(state.entriesMap);
            for (const entry of entriesWithBig) {
                newMap.set(getEntryKey(entry), entry);
            }

            return {
                ...state,
                entriesMap: newMap,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          entries: [...state.periodData.entries, ...entries],
                      }
                    : null,
            };
        });
    },

    batchUpsertEntries: (entries: EntryWithBigValue[]) => {
        set(state => {
            const newEntriesMap = new Map(state.entriesMap);
            const dtoEntries: EntryDto[] = [];

            for (const entry of entries) {
                const key = getEntryKey(entry);
                newEntriesMap.set(key, entry);
                dtoEntries.push({
                    ...entry,
                    value: Number(entry.value.toString()),
                });
            }

            if (!state.periodData) {
                return { ...state, entriesMap: newEntriesMap };
            }

            const updatedPeriodEntries = state.periodData.entries.map(existing => {
                const key = getEntryKey(existing);
                const replacement = dtoEntries.find(d => getEntryKey(d) === key);
                return replacement ?? existing;
            });

            // Add any entries that didn't exist in periodData yet
            for (const dto of dtoEntries) {
                const key = getEntryKey(dto);
                if (!state.periodData.entries.some(e => getEntryKey(e) === key)) {
                    updatedPeriodEntries.push(dto);
                }
            }

            return {
                ...state,
                entriesMap: newEntriesMap,
                periodData: {
                    ...state.periodData,
                    entries: updatedPeriodEntries,
                },
            };
        });
    },

    // ========================================================================
    // Task CRUD Actions
    // ========================================================================

    addTask: (task: TaskDtoV2) => {
        set(state => {
            if (!state.periodData) {
                return {
                    ...state,
                    tasks: [...state.tasks, task],
                };
            }

            const dates = generateDateRange(state.periodData.startDate, state.periodData.endDate);
            const newEntries = fillMissingEntries(state.periodData.entries, [task], dates, state.periodData.id);

            const newBigEntries = newEntries
                .filter(e => e.taskId === task.id)
                .map(entry => ({
                    ...entry,
                    date: normalizeDate(entry.date),
                    value: new Big(entry.value?.toString() || '0'),
                }));

            const newMap = new Map(state.entriesMap);
            for (const entry of newBigEntries) {
                const key = getEntryKey(entry);
                if (!newMap.has(key)) {
                    newMap.set(key, entry);
                }
            }

            return {
                ...state,
                entriesMap: newMap,
                tasks: [...state.tasks, task],
                periodData: {
                    ...state.periodData,
                    tasks: [...state.periodData.tasks, task],
                    entries: newEntries,
                },
            };
        });
    },

    updateTask: (taskId: string, workType: string, updates: Partial<TaskDtoV2>) => {
        const matchesRow = (task: TaskDtoV2) => task.id === taskId && (task.workType || 'default') === (workType || 'default');

        set(state => {
            const updatedTasks = state.tasks.map(task => (matchesRow(task) ? { ...task, ...updates } : task));

            return {
                ...state,
                tasks: updatedTasks,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          tasks: state.periodData.tasks.map(task => (matchesRow(task) ? { ...task, ...updates } : task)),
                      }
                    : null,
            };
        });
    },

    removeTask: (taskId: string, workType = 'default') => {
        const matchesEntry = (entry: { taskId: string; workTypeId?: string }) => entry.taskId === taskId && (entry.workTypeId || 'default') === workType;
        const matchesTask = (task: TaskDtoV2) => task.id === taskId && (task.workType || 'default') === workType;

        set(state => {
            const newMap = new Map(state.entriesMap);
            for (const [key, entry] of newMap) {
                if (matchesEntry(entry)) {
                    newMap.delete(key);
                }
            }

            return {
                ...state,
                tasks: state.tasks.filter(task => !matchesTask(task)),
                entriesMap: newMap,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          tasks: state.periodData.tasks.filter(task => !matchesTask(task)),
                          entries: state.periodData.entries.filter(entry => !matchesEntry(entry)),
                      }
                    : null,
            };
        });
    },

    // ========================================================================
    // Project CRUD Actions
    // ========================================================================

    addProject: (project: ProjectDtoV2) => {
        set(state => {
            let updatedAllocationInfo = state.allocationInfo;
            let projectWithAllocation = project;

            // If the project is in otherProjects, transfer its allocation
            if (updatedAllocationInfo?.otherProjects?.length) {
                const otherProject = updatedAllocationInfo.otherProjects.find(p => p.id === project.id);
                if (otherProject) {
                    projectWithAllocation = { ...project, allocation: otherProject.allocation, workTypes: otherProject.workTypes };
                    updatedAllocationInfo = {
                        ...updatedAllocationInfo,
                        otherProjects: updatedAllocationInfo.otherProjects.filter(p => p.id !== project.id),
                    };
                }
            }

            return {
                ...state,
                projects: [...state.projects, projectWithAllocation],
                allocationInfo: updatedAllocationInfo,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          projects: [...state.periodData.projects, projectWithAllocation],
                          allocationInfo: updatedAllocationInfo ?? state.periodData.allocationInfo,
                      }
                    : null,
            };
        });
    },

    updateProject: (projectId: string, updates: Partial<ProjectDtoV2>) => {
        set(state => {
            const updatedProjects = state.projects.map(project => (project.id === projectId ? { ...project, ...updates } : project));

            return {
                ...state,
                projects: updatedProjects,
                periodData: state.periodData
                    ? {
                          ...state.periodData,
                          projects: state.periodData.projects.map(project => (project.id === projectId ? { ...project, ...updates } : project)),
                      }
                    : null,
            };
        });
    },

    // ========================================================================
    // Expand/Collapse Actions
    // ========================================================================

    toggleProjectExpanded: (projectId: string) => {
        set(state => {
            const newExpanded = new Set(state.expandedProjectIds);
            if (newExpanded.has(projectId)) {
                newExpanded.delete(projectId);
            } else {
                newExpanded.add(projectId);
            }
            saveExpandedIds(
                state.projects.map(p => p.id),
                newExpanded,
            );
            return { ...state, expandedProjectIds: newExpanded };
        });
    },

    setProjectExpanded: (projectId: string, expanded: boolean) => {
        set(state => {
            const newExpanded = new Set(state.expandedProjectIds);
            if (expanded) {
                newExpanded.add(projectId);
            } else {
                newExpanded.delete(projectId);
            }
            saveExpandedIds(
                state.projects.map(p => p.id),
                newExpanded,
            );
            return { ...state, expandedProjectIds: newExpanded };
        });
    },

    expandAllProjects: () => {
        set(state => {
            const newExpanded = new Set(state.projects.map(project => project.id));
            saveExpandedIds(
                state.projects.map(p => p.id),
                newExpanded,
            );
            return {
                ...state,
                expandedProjectIds: newExpanded,
            };
        });
    },

    collapseAllProjects: () => {
        set(state => {
            saveExpandedIds(
                state.projects.map(p => p.id),
                new Set<string>(),
            );
            return {
                ...state,
                expandedProjectIds: new Set<string>(),
            };
        });
    },

    // ========================================================================
    // Pin Actions
    // ========================================================================

    toggleTaskPinned: (taskId: string, workType: string) => {
        const key = `${taskId}_${workType || 'default'}`;
        set(state => {
            const newPinned = new Set(state.pinnedTaskKeys);
            if (newPinned.has(key)) {
                newPinned.delete(key);
            } else {
                newPinned.add(key);
            }
            return { ...state, pinnedTaskKeys: newPinned };
        });
    },

    setTaskPinned: (taskId: string, workType: string, pinned: boolean) => {
        const key = `${taskId}_${workType || 'default'}`;
        set(state => {
            const newPinned = new Set(state.pinnedTaskKeys);
            if (pinned) {
                newPinned.add(key);
            } else {
                newPinned.delete(key);
            }
            return { ...state, pinnedTaskKeys: newPinned };
        });
    },

    // ========================================================================
    // Highlight Actions
    // ========================================================================

    setHighlightedTask: (taskId: string | null) => {
        set({ highlightedTaskId: taskId });
    },

    // ========================================================================
    // Submit Success Actions
    // ========================================================================

    setShowSubmitSuccess: (show: boolean) => {
        set({ showSubmitSuccess: show });
    },
}));

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Get all entries as array
 */
export const useAllEntries = () => useGridStore(state => entriesArray(state.entriesMap));

/**
 * Get a single entry by its composite key (taskId + date + workTypeId)
 * Uses value-based equality to avoid re-renders when the entry data hasn't changed
 */
export const useEntry = (taskId: string, date: string, workTypeId: string) =>
    useGridStore(state => state.entriesMap.get(`${taskId}_${date}_${workTypeId || 'default'}`), entryEq);

/**
 * Get entries by task
 */
export const useEntriesByTask = (taskId: string) => useGridStore(state => entriesArray(state.entriesMap).filter(entry => entry.taskId === taskId), shallow);

/**
 * Get entries by date
 */
export const useEntriesByDate = (date: string) => useGridStore(state => entriesArray(state.entriesMap).filter(entry => entry.date === date), shallow);

/**
 * Get entries by project (through tasks)
 */
export const useEntriesByProject = (projectId: string) =>
    useGridStore(state => {
        const taskIds = new Set(state.tasks.filter(task => task.projectId === projectId).map(task => task.id));
        return entriesArray(state.entriesMap).filter(entry => taskIds.has(entry.taskId));
    }, shallow);

/**
 * Get single project by ID
 */
export const useProject = (projectId: string) => useGridStore(state => state.projects.find(project => project.id === projectId));

/**
 * Get all projects as array
 */
export const useAllProjects = () => useGridStore(state => state.projects);

/**
 * Get single task by ID
 */
export const useTask = (taskId: string) => useGridStore(state => state.tasks.find(task => task.id === taskId));

/**
 * Get a specific task row matching both taskId and workType
 */
export const useTaskRow = (taskId: string, workType: string) =>
    useGridStore(state => state.tasks.find(task => task.id === taskId && (task.workType || 'default') === (workType || 'default')));

/**
 * Get all tasks as array
 */
export const useAllTasks = () => useGridStore(state => state.tasks);

/**
 * Get tasks by project
 */
export const useTasksByProject = (projectId: string) => useGridStore(state => state.tasks.filter(task => task.projectId === projectId), shallow);

/**
 * Get working hours by date
 */
export const useWorkingHoursByDate = (date: string) => useGridStore(state => state.workingHours.find(wh => wh.date === date));

/**
 * Get all working hours as array
 */
export const useAllWorkingHours = () => useGridStore(state => state.workingHours);

/**
 * Get sorted entries
 */
export const useSortedEntries = (sortBy: SortField = 'date', direction: SortDirection = 'asc') =>
    useGridStore(state => {
        // Create a copy for sorting
        const entries = entriesArray(state.entriesMap);

        return entries.sort((a, b) => {
            let comparison = 0;

            switch (sortBy) {
                case 'date': {
                    comparison = a.date.localeCompare(b.date);
                    break;
                }
                case 'value': {
                    comparison = a.value.cmp(b.value);
                    break;
                }
                case 'taskName': {
                    const taskA = state.tasks.find(task => task.id === a.taskId)?.taskName || '';
                    const taskB = state.tasks.find(task => task.id === b.taskId)?.taskName || '';
                    comparison = taskA.localeCompare(taskB);
                    break;
                }
                case 'projectName': {
                    const taskA = state.tasks.find(task => task.id === a.taskId);
                    const taskB = state.tasks.find(task => task.id === b.taskId);
                    const projectA = state.projects.find(project => project.id === taskA?.projectId)?.name || '';
                    const projectB = state.projects.find(project => project.id === taskB?.projectId)?.name || '';
                    comparison = projectA.localeCompare(projectB);
                    break;
                }
            }

            return direction === 'asc' ? comparison : -comparison;
        });
    }, shallow);

/**
 * Get total hours by task
 */
export const useTotalHoursByTask = (taskId: string) =>
    useGridStore(
        state =>
            entriesArray(state.entriesMap)
                .filter(entry => entry.taskId === taskId)
                .reduce((sum, entry) => sum.plus(entry.value), bigZero),
        bigEq,
    );

/**
 * Get total hours by task and work type
 */
export const useTotalHoursByTaskAndWorkType = (taskId: string, workType: string) =>
    useGridStore(
        state =>
            entriesArray(state.entriesMap)
                .filter(entry => entry.taskId === taskId && (entry.workTypeId || 'default') === (workType || 'default'))
                .reduce((sum, entry) => sum.plus(entry.value), bigZero),
        bigEq,
    );

/**
 * Get total hours by project
 */
export const useTotalHoursByProject = (projectId: string) =>
    useGridStore(state => {
        const taskIds = new Set(state.tasks.filter(task => task.projectId === projectId).map(task => task.id));
        return entriesArray(state.entriesMap)
            .filter(entry => taskIds.has(entry.taskId))
            .reduce((sum, entry) => sum.plus(entry.value), bigZero);
    }, bigEq);

/**
 * Get total hours by date
 */
export const useTotalHoursByDate = (date: string) =>
    useGridStore(
        state =>
            entriesArray(state.entriesMap)
                .filter(entry => entry.date === date)
                .reduce((sum, entry) => sum.plus(entry.value), bigZero),
        bigEq,
    );

/**
 * Get grand total hours (all entries in the period)
 */
export const useGrandTotalHours = () => useGridStore(state => entriesArray(state.entriesMap).reduce((sum, entry) => sum.plus(entry.value), bigZero), bigEq);

/**
 * Get all dates in period
 */
export const usePeriodDates = () =>
    useGridStore(state => {
        if (!state.periodData) return [];
        return generateDateRange(state.periodData.startDate, state.periodData.endDate);
    }, shallow);

/**
 * Get period data
 */
export const usePeriodData = () => useGridStore(state => state.periodData);

/**
 * Get daily totals for a specific project
 * Returns a map of date -> total hours for that project
 */
export const useDailyTotalsByProject = (projectId: string) =>
    useGridStore(state => {
        if (!state.periodData) return {} as Record<string, Big>;
        const entries = entriesArray(state.entriesMap);
        const taskIds = new Set(state.tasks.filter(task => task.projectId === projectId).map(task => task.id));
        const dates = generateDateRange(state.periodData.startDate, state.periodData.endDate);

        return dates.reduce((totals, date) => {
            totals[date] = entries.filter(entry => taskIds.has(entry.taskId) && entry.date === date).reduce((sum, entry) => sum.plus(entry.value), bigZero);
            return totals;
        }, {} as Record<string, Big>);
    }, shallowBigRecord);

/**
 * Get entries for a specific task as a map keyed by date
 * Returns Record<date, EntryWithBigValue>
 */
export const useEntriesMapByTask = (taskId: string) =>
    useGridStore(state => {
        return entriesArray(state.entriesMap)
            .filter(entry => entry.taskId === taskId)
            .reduce((map, entry) => {
                map[entry.date] = entry;
                return map;
            }, {} as Record<string, EntryWithBigValue>);
    }, shallow);

/**
 * Get entries for a specific task and work type as a map keyed by date
 * Returns Record<date, EntryWithBigValue>
 */
export const useEntriesMapByTaskAndWorkType = (taskId: string, workType: string) =>
    useGridStore(state => {
        return entriesArray(state.entriesMap)
            .filter(entry => entry.taskId === taskId && (entry.workTypeId || 'default') === (workType || 'default'))
            .reduce((map, entry) => {
                map[entry.date] = entry;
                return map;
            }, {} as Record<string, EntryWithBigValue>);
    }, shallow);

/**
 * Get entries by task and work type
 */
export const useEntriesByTaskAndWorkType = (taskId: string, workType: string) =>
    useGridStore(
        state => entriesArray(state.entriesMap).filter(entry => entry.taskId === taskId && (entry.workTypeId || 'default') === (workType || 'default')),
        shallow,
    );

/**
 * Get daily totals across all projects
 * Returns a map of date -> total hours for all entries
 */
export const useDailyTotalsAllProjects = () =>
    useGridStore(state => {
        if (!state.periodData) return {} as Record<string, Big>;
        const entries = entriesArray(state.entriesMap);
        const dates = generateDateRange(state.periodData.startDate, state.periodData.endDate);

        return dates.reduce((totals, date) => {
            totals[date] = entries.filter(entry => entry.date === date).reduce((sum, entry) => sum.plus(entry.value), bigZero);
            return totals;
        }, {} as Record<string, Big>);
    }, shallowBigRecord);

/**
 * Get working hours as a map keyed by date
 * Returns Record<date, hours>
 */
export const useWorkingHoursMap = () =>
    useGridStore(state => {
        return state.workingHours.reduce((map, workingHour) => {
            // Calculate hours from start/end times or use default
            map[workingHour.date] = 8; // Default, calculate from workingHour.start/end if available
            return map;
        }, {} as Record<string, number>);
    }, shallow);

/**
 * Check if a project is expanded
 */
export const useIsProjectExpanded = (projectId: string) => useGridStore(state => state.expandedProjectIds.has(projectId));

/**
 * Check if all projects are expanded
 */
export const useAreAllProjectsExpanded = () => useGridStore(state => state.projects.length > 0 && state.expandedProjectIds.size === state.projects.length);

/**
 * Get total allocation for the period
 */
export const useTotalAllocation = () => useGridStore(state => state.allocationInfo?.totalAllocation ?? null);

/**
 * Get projects with allocations that are not in the grid
 */
export const useOtherAllocatedProjects = () => useGridStore(state => state.allocationInfo?.otherProjects ?? [], shallow);

/**
 * Get projects in the grid that have allocations with registered hours below the allocation.
 * Returns an array of { project, registered, below } for projects with a deficit.
 */
export type ProjectAllocationDeficit = {
    project: ProjectDtoV2;
    allocated: number;
    registered: number;
    below: number;
};

export const useGridProjectsWithAllocationDeficit = () =>
    useGridStore(state => {
        const result: ProjectAllocationDeficit[] = [];

        for (const project of state.projects) {
            if (project.allocation == null || project.allocation <= 0) continue;

            const taskIds = new Set(state.tasks.filter(t => t.projectId === project.id).map(t => t.id));
            const registered = entriesArray(state.entriesMap)
                .filter(e => taskIds.has(e.taskId))
                .reduce((sum, e) => sum.plus(e.value), bigZero)
                .toNumber();

            const below = project.allocation - registered;
            if (below > 0) {
                result.push({ project, allocated: project.allocation, registered, below });
            }
        }

        return result;
    }, shallow);

/**
 * Get the currently highlighted task ID (for scroll-to highlight animation)
 */
export const useHighlightedTaskId = () => useGridStore(s => s.highlightedTaskId);

/**
 * Check if a task is pinned
 */
export const useIsTaskPinned = (taskId: string, workType: string) => useGridStore(state => state.pinnedTaskKeys.has(`${taskId}_${workType || 'default'}`));

/**
 * Derive whether the grid is read-only based on timesheet and period state.
 * Editable when timesheetState is Open or Rejected AND periodState is not Closed.
 */
export const useIsGridReadOnly = () =>
    useGridStore(state => {
        const timesheetState = state.periodData?.timesheetState;
        const periodState = state.periodData?.periodState;
        if (!timesheetState || !periodState) return true;
        const isEditable = (timesheetState === 'Open' || timesheetState === 'Rejected') && periodState !== 'Closed';
        return !isEditable;
    });

export const useShowSubmitSuccess = () => useGridStore(s => s.showSubmitSuccess);
